import { Component } from '@theme/component';
import { onMotionChange, sitePaused } from '@theme/bdc-motion';

/**
 * Time from the start of one move to the start of the next. The accessibility design
 * review caps the pace at one move every 2.5s, so do not lower it.
 */
const MOVE_INTERVAL = 2500;

/** From the grid being free to move to its first move, so it comes alive soon after it shows */
const START_DELAY = 800;

/** The pause between a logo dissolving out and back in, so the move reads as travel */
const MOVE_GAP = 250;

/** Longer than the CSS fade (650ms), in case `transitionend` never fires. */
const FADE_FALLBACK = 800;

/**
 * Braun Design Co cycling logo grid (sections/bdc-logo-grid.liquid, motion mode).
 *
 * The section renders two things side by side:
 * - the real list of client logos, for screen readers and search engines. It never
 *   changes, and from 750px with motion allowed the CSS clips it away from the first
 *   paint;
 * - the stage, a decorative copy (`aria-hidden`, `inert` outside the theme editor): 16
 *   cells holding the same logos plus a few empty ones. The CSS shows it from the first
 *   paint, so nothing shifts when this module arrives.
 *
 * This module moves one logo at a time into an empty cell: it dissolves out (a fade with a
 * slight blur, as on Payload's enterprise page), pauses, moves to the empty cell (the
 * same element, so the image is not fetched again) and dissolves back in. The first move
 * comes 0.8s after the grid is free to move, then one every 2.5 seconds, in a shuffled
 * turn order in which every logo holds still for at least three turns (7.5s).
 *
 * It only moves while all of these hold:
 * - the device does not ask for reduced motion, and the grid is not paused (`data-paused`):
 *   by its own button, or by the site-wide pause (the footer's "Pause animations",
 *   `@theme/bdc-motion`), which pauses it when the page loads paused and whenever it is
 *   turned on, and lifts that when turned off unless the visitor pressed this Pause. The
 *   grid keeps its layout and its button, and Play starts it for this page view only;
 * - the tab is visible and the stage is on screen (below the sticky header);
 * - no other running loop is on screen, so two loops never move together. A loop is an
 *   element marked `data-bdc-loop` (the part that moves, such as the homepage hero's
 *   drifting row); it is running while the component around it has `data-ready` and no
 *   `data-paused`;
 * - the pointer is not over the grid, and no logo block is selected in the theme
 *   editor. The button is left out of the hover hold, and focus holds nothing (the button
 *   is the only focusable part), so a click or keypress on Play always restarts the grid.
 *
 * Stopping lets a move already under way finish. Starting again waits 0.8s, or longer if
 * the last move began under 2.5s ago, so moves are always at least 2.5s apart and there is
 * never a burst. When reduced motion is turned on, a move under way is dropped and its logo
 * shown at once.
 *
 * The grid is itself a loop: the stage carries `data-bdc-loop`, and this element
 * `data-ready` while it can move (which shows the Pause button) and `data-paused` once
 * the visitor presses Pause.
 * The button's visually hidden label carries the state, as on the hero (no `aria-pressed`).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} controls - The wrapper around the button, invisible until `data-ready`.
 * @property {HTMLButtonElement} toggle - The Pause/Play button, with the two labels in `data-label-pause` and `data-label-play`.
 * @property {HTMLElement} label - The button's visually hidden name.
 * @property {HTMLElement} stage - The decorative grid of cells.
 *
 * @extends {Component<Refs>}
 */
class BdcLogoCycle extends Component {
  requiredRefs = ['controls', 'toggle', 'label', 'stage'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {MutationObserver | undefined} */
  #loopObserver;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Bumped to drop a move under way. */
  #moveId = 0;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Whether the visitor pressed this grid's own Pause (this page view only). */
  #pausedByVisitor = false;

  /** @type {boolean | undefined} The site-wide pause last applied to the grid. */
  #sitePaused;

  #moving = false;
  #stageInView = false;
  #hovered = false;
  #blockSelected = false;

  /** @type {Set<Element>} Other loops currently on screen. */
  #loopsInView = new Set();

  /** @type {HTMLElement[]} Logos still to move in this round. */
  #turns = [];

  /** @type {HTMLElement[]} The last two logos that moved, oldest first */
  #recent = [];
  /** When the last move started, for the 2.5s spacing across restarts */
  #lastMoveAt = -Infinity;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    onMotionChange(this.#syncMotion, { signal });
    document.addEventListener('visibilitychange', this.#update, { signal });
    // On this element, not the stage: an inert stage gets no pointer events of its own
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    // Theme editor: a re-rendered section (such as the hero) brings a new loop element
    document.addEventListener('shopify:section:load', this.#watchScreen, { signal });

    this.#watchScreen();
    this.#syncMotion();
    this.#renderLabel();
  }

  updatedCallback() {
    super.updatedCallback();

    this.#syncMotion();
    this.#renderLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#intersectionObserver?.disconnect();
    this.#loopObserver?.disconnect();
    this.#stop();
  }

  /**
   * Whether the visitor has paused the grid with the button.
   *
   * @returns {boolean}
   */
  get paused() {
    return this.hasAttribute('data-paused');
  }

  /**
   * Pauses or resumes the grid. Bound to the button with `on:click`.
   */
  togglePlayback() {
    this.#pausedByVisitor = !this.paused;
    this.toggleAttribute('data-paused', this.#pausedByVisitor);
    this.#renderLabel();
    this.#update();
  }

  /** Whether every condition for moving holds right now. */
  get #shouldRun() {
    return (
      this.hasAttribute('data-ready') &&
      !this.paused &&
      !document.hidden &&
      this.#stageInView &&
      !this.#hovered &&
      !this.#blockSelected &&
      ![...this.#loopsInView].some(isRunningLoop)
    );
  }

  /**
   * Watches what is on screen: the stage, and every other loop and whether it is running.
   * Run again whenever the theme editor re-renders a section.
   */
  #watchScreen = () => {
    this.#intersectionObserver?.disconnect();
    this.#loopObserver?.disconnect();
    this.#loopsInView.clear();

    const otherLoops = [...document.querySelectorAll('[data-bdc-loop]')].filter((loop) => !this.contains(loop));

    // The header is always sticky, so anything behind it counts as off screen
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    this.#intersectionObserver = new IntersectionObserver(this.#onIntersect, {
      rootMargin: `-${Math.round(headerHeight) || 0}px 0px 0px 0px`,
    });
    this.#intersectionObserver.observe(this.refs.stage);
    for (const loop of otherLoops) this.#intersectionObserver.observe(loop);

    this.#loopObserver = new MutationObserver(this.#update);
    for (const loop of otherLoops) {
      this.#loopObserver.observe(loopHost(loop), { attributes: true, attributeFilter: ['data-ready', 'data-paused'] });
    }
  };

  /**
   * Shows the button, and lets the grid move (`data-ready`), unless the device asks for
   * reduced motion. Pauses the grid when the site-wide pause is turned on, and lifts that
   * when it is turned off.
   */
  #syncMotion = () => {
    const allowed = !this.#reducedMotion.matches;

    this.refs.controls.hidden = !allowed;
    this.toggleAttribute('data-ready', allowed);

    const paused = sitePaused();
    if (paused !== this.#sitePaused) {
      this.#sitePaused = paused;
      this.toggleAttribute('data-paused', paused || this.#pausedByVisitor);
      this.#renderLabel();
    }

    if (!allowed) this.#stop();
    this.#update();
  };

  /** Starts the timer when the grid may move, and stops it when it may not. */
  #update = () => {
    if (!this.#shouldRun) {
      clearTimeout(this.#timer);
      this.#timer = undefined;
      return;
    }

    if (this.#timer === undefined && !this.#moving) {
      const sinceLastMove = performance.now() - this.#lastMoveAt;
      this.#timer = setTimeout(this.#move, Math.max(START_DELAY, MOVE_INTERVAL - sinceLastMove));
    }
  };

  /** Stops at once: clears the timer and drops a move under way, showing its logo. */
  #stop() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#moveId += 1;
    this.#moving = false;

    for (const tile of this.#tiles) delete tile.dataset.state;
  }

  /** Moves one logo into an empty cell, then schedules the next move. */
  #move = async () => {
    this.#timer = undefined;
    if (!this.#shouldRun) return;

    const tile = this.#nextTile();
    const holes = this.#slots.filter((slot) => !slot.firstElementChild);
    const hole = holes[Math.floor(Math.random() * holes.length)];
    if (!tile || !hole) return;

    const moveId = ++this.#moveId;
    const startedAt = performance.now();
    this.#lastMoveAt = startedAt;
    this.#recent = [...this.#recent, tile].slice(-2);
    this.#moving = true;

    tile.dataset.state = 'out';
    await fadeEnd(tile);
    if (moveId !== this.#moveId) return;
    await new Promise((resolve) => setTimeout(resolve, MOVE_GAP));
    if (moveId !== this.#moveId) return;

    hole.append(tile);
    // Commit the faded-out style in its new cell, so removing the state fades it in
    void getComputedStyle(tile).opacity;
    delete tile.dataset.state;

    this.#moving = false;

    if (this.#shouldRun) {
      const wait = Math.max(0, MOVE_INTERVAL - (performance.now() - startedAt));
      this.#timer = setTimeout(this.#move, wait);
    }
  };

  /**
   * The next logo in a shuffled round. A new round never gives its first two turns to the
   * last two logos that moved, so every logo holds still for at least three turns (7.5s).
   *
   * @returns {HTMLElement | undefined}
   */
  #nextTile() {
    if (this.#turns.length === 0) {
      const turns = shuffle(this.#tiles);

      // The last two logos that moved don't take the new round's first two turns (popped
      // from the end), so every logo holds still for at least three turns: 7.5s
      for (let end = turns.length - 1; end >= Math.max(turns.length - 2, 0); end -= 1) {
        if (!this.#recent.includes(turns[end])) continue;
        const swap = turns.findIndex((tile, i) => i < turns.length - 2 && !this.#recent.includes(tile));
        if (swap >= 0) [turns[swap], turns[end]] = [turns[end], turns[swap]];
      }

      this.#turns = turns;
    }

    return this.#turns.pop();
  }

  /** @returns {HTMLElement[]} */
  get #slots() {
    return [...this.refs.stage.querySelectorAll('.bdc-logos__slot')];
  }

  /** @returns {HTMLElement[]} */
  get #tiles() {
    return [...this.refs.stage.querySelectorAll('.bdc-logos__tile')];
  }

  /** Puts the label that matches the current state on the button. */
  #renderLabel() {
    const { toggle, label } = this.refs;
    const text = this.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;

    if (text && label.textContent !== text) label.textContent = text;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target === this.refs.stage) {
        this.#stageInView = entry.isIntersecting;
      } else if (entry.isIntersecting) {
        this.#loopsInView.add(entry.target);
      } else {
        this.#loopsInView.delete(entry.target);
      }
    }

    this.#update();
  };

  /**
   * Holds the grid while the pointer is over it, but not over the button.
   *
   * @param {PointerEvent} event
   */
  #onPointerOver = (event) => {
    this.#hovered = !(event.target instanceof Node && this.refs.controls.contains(event.target));
    this.#update();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#update();
  };

  /**
   * Theme editor: holds the grid still, with the selected logo showing, while a logo
   * block is selected.
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    this.#blockSelected = true;
    this.#stop();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    this.#blockSelected = false;
    this.#update();
  };
}

/**
 * The component that runs a loop: the nearest custom element around it (or the loop itself).
 *
 * @param {Element} loop
 * @returns {Element}
 */
function loopHost(loop) {
  for (let element = loop; element; element = element.parentElement) {
    if (element.localName.includes('-')) return element;
  }

  return loop;
}

/**
 * Whether another loop is moving: its component is ready and not paused.
 *
 * @param {Element} loop
 * @returns {boolean}
 */
function isRunningLoop(loop) {
  const host = loopHost(loop);

  return host.hasAttribute('data-ready') && !host.hasAttribute('data-paused');
}

/**
 * Resolves when the element's opacity transition ends, or after a fallback delay.
 *
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
function fadeEnd(element) {
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(fallback);
      element.removeEventListener('transitionend', onEnd);
      resolve();
    };
    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target === element && event.propertyName === 'opacity') done();
    };
    const fallback = setTimeout(done, FADE_FALLBACK);

    element.addEventListener('transitionend', onEnd);
  });
}

/**
 * A shuffled copy (Fisher-Yates).
 *
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
function shuffle(items) {
  const copy = [...items];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
}

if (!customElements.get('bdc-logo-cycle')) {
  customElements.define('bdc-logo-cycle', BdcLogoCycle);
}
